// Workspace de UN pedido de venta.
//
// La lista sirve para encontrar pedidos; esto es para trabajar sobre uno solo.
// Empezó como una página larga con navegación por anclas, y con un pedido real
// —18 cargas, 83 líneas, 15 facturas— medía 7.000 px: la altura crecía con la
// SUMA de todas sus secciones, que es el problema que la pantalla vino a
// resolver. Ahora las pestañas montan una sección cada vez y la altura depende
// sólo de lo que se está mirando.
//
// Los contadores viven en las pestañas para que el tamaño del pedido siga a la
// vista aunque su sección no lo esté. El estado va en la URL: refrescar no
// devuelve al resumen y se puede pasar un enlace a una vista concreta.
//
// No duplica flujos: adjuntos, generación documental y creación de carga abren
// exactamente los mismos cajones que la lista.
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { RUTA_DETALLE_PEDIDO_KEY, almacenSesion, guardarRutaRecordada, olvidarRutaRecordada } from '@/utils/filtrosPersistentes'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { usePedidoVentaDetalle } from '@/hooks/usePedidoVentaDetalle'
import { cabeceraDeLineas, filaDeCabecera, useCabeceraPedidoVenta } from '@/hooks/useCabeceraPedidoVenta'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { exportarPedidosVenta } from '@/utils/exportarPedidosVenta'
import { pedidosVentasApi } from '@/services/api'
import { agruparPorCarga, type ResumenCarga } from '@/utils/cargasPicking'
import { agruparPorFactura } from '@/utils/facturasPedido'
import { Loader } from '@/components/common/Loader'
import { useMarcasFilas } from '@/components/common/MarcasFilas'
import { EmptyState } from '@/components/common/EmptyState'
import { AdjuntosModal } from '@/components/pedidosCompra/AdjuntosModal'
import { CargaDocumentosDrawer } from '@/components/pedidos-ventas/CargaDocumentosDrawer'
import { CabeceraPedido } from '@/components/pedidos-ventas/detalle/CabeceraPedido'
import { TabsPedido, type DefinicionTab } from '@/components/pedidos-ventas/detalle/TabsPedido'
import { ResumenBento, type AlertaPedido } from '@/components/pedidos-ventas/detalle/ResumenBento'
import { SeccionCargas } from '@/components/pedidos-ventas/detalle/SeccionCargas'
import { SeccionLineas } from '@/components/pedidos-ventas/detalle/SeccionLineas'
import { SeccionFacturacion } from '@/components/pedidos-ventas/detalle/SeccionFacturacion'
import { SeccionDocumentacion } from '@/components/pedidos-ventas/detalle/SeccionDocumentacion'
import { useTabsPedido } from '@/components/pedidos-ventas/detalle/useTabsPedido'
import { fmtQty } from '@/components/pedidos-ventas/formato'
import type { FiltroEstadoCarga } from '@/types'

/** Un pedido cerrado o cancelado ya no admite cargas: mismo criterio que la lista. */
const ESTADO_ABIERTO = 'Pedido abierto'

export default function PedidoVentaDetallePage() {
  const params = useParams<{ empresa: string; salesid: string }>()
  const empresa = decodeURIComponent(params.empresa ?? '')
  const salesid = decodeURIComponent(params.salesid ?? '')

  // key: al cambiar de pedido se remonta todo el subárbol en vez de arrastrar el
  // estado de filtros, orden y paginación del anterior.
  return <Detalle key={`${empresa}/${salesid}`} empresa={empresa} salesid={salesid} />
}

function Detalle({ empresa, salesid }: { empresa: string; salesid: string }) {
  const { dark } = useAppStore()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const isEmpresaAll = useIsEmpresaAll()
  const fmtArt = useFmtArticulo()
  const tabs = useTabsPedido()
  // El ámbito es el PEDIDO, no la pestaña: los dos grupos de líneas comparten
  // almacén, así que una línea marcada entre las pendientes sigue marcada si
  // reaparece en las facturadas —que es justo lo que hace la vista de Fabric.
  const marcasLineas = useMarcasFilas(`pedidoVentaLineas:${empresa}/${salesid}`)

  const [filtroCarga, setFiltroCarga] = useState<FiltroEstadoCarga>('')
  const [nuevaCarga, setNuevaCarga] = useState(false)
  const [exportando, setExportando] = useState(false)
  const [adjuntosAbierto, setAdjuntosAbierto] = useState(false)
  const [facturaAdj, setFacturaAdj] = useState<{ recid: string; factura: string } | null>(null)
  const [documentos, setDocumentos] = useState<ResumenCarga | null>(null)

  // La cabecera va primero: su estado decide si procede consultar solicitudes de
  // carga, que es una consulta cara (cruza Fabric y Dataverse).
  const base = useCabeceraPedidoVenta(empresa, salesid)
  const puedeCrearCarga = base.cabecera?.estado === ESTADO_ABIERTO

  const detalle = usePedidoVentaDetalle(salesid, { puedeCrearCarga })
  const {
    lineas, cargas, solicitudes, pickingPorLinea, lineaPorTransid,
    descripcionPorTransid, lanzamientoDe, albaranesDe, sinFacturar, facturadas,
    qtySinLanzar,
  } = detalle

  // Último escalón de la cascada: sin fila del state ni de la lista, se
  // reconstruye de las líneas y la cabecera avisa de que va incompleta.
  const cabecera = useMemo(
    () => base.cabecera ?? (lineas.length ? cabeceraDeLineas(empresa, salesid, lineas) : null),
    [base.cabecera, lineas, empresa, salesid],
  )
  const origen = base.origen ?? (cabecera ? 'derivada' : null)
  const { volverA, isLoading: cargandoCabecera } = base

  const porCarga = useMemo(() => agruparPorCarga(cargas), [cargas])
  const facturas = useMemo(
    () => agruparPorFactura(lineas).map(f => f.factura),
    [lineas],
  )

  // Sólo alertas que los datos sostienen. Nada de estados inventados.
  const alertas = useMemo<AlertaPedido[]>(() => {
    const out: AlertaPedido[] = []
    if (!cabecera) return out

    const hoy = new Date().toISOString().slice(0, 10)
    if (cabecera.fechaEntrega && cabecera.fechaEntrega < hoy && cabecera.qtyPendiente > 0) {
      out.push({
        clave: 'vencida',
        tono: 'rojo',
        texto: t('salesOrders.detalle.alertaVencida', {
          fecha: cabecera.fechaEntrega.split('-').reverse().join('/'),
          pendiente: fmtQty(cabecera.qtyPendiente),
        }),
      })
    }

    // Lanzar de más no rompe nada, pero conviene verlo antes de expedir.
    const conExceso = sinFacturar.filter(l => lanzamientoDe(l).exceso > 0)
    if (conExceso.length > 0) {
      out.push({
        clave: 'exceso',
        tono: 'ambar',
        texto: t('salesOrders.detalle.alertaExceso', { count: conExceso.length }),
      })
    }

    if (solicitudes.length > 0) {
      out.push({
        clave: 'solicitudes',
        tono: 'ambar',
        texto: t('salesOrders.detalle.alertaSolicitudes', { count: solicitudes.length }),
      })
    }
    return out
  }, [cabecera, sinFacturar, lanzamientoDe, solicitudes, t])

  const definicionTabs: DefinicionTab[] = [
    { id: 'resumen', etiqueta: t('salesOrders.detalle.resumen') },
    { id: 'cargas', etiqueta: t('salesOrders.picking.title'), cuenta: porCarga.length },
    { id: 'lineas', etiqueta: t('salesOrders.detalle.lineasTitulo'), cuenta: lineas.length },
    { id: 'facturacion', etiqueta: t('salesOrders.billing.title'), cuenta: facturas.length },
    { id: 'documentos', etiqueta: t('salesOrders.documentos.title') },
  ]

  // Recuerdo de dónde estaba: la lista lo repone si se entra a ella pelada.
  //
  // SÓLO se recuerda un detalle que existe. Un pedido que no se encuentra —o al
  // que se ha perdido el acceso— no puede quedar guardado: la lista lo repondría,
  // volveríamos a la pantalla de «no encontrado», y como la reposición es
  // `replace` no habría forma de salir ni con «Atrás». Un bucle sin salida.
  useEffect(() => {
    if (!cabecera) return
    guardarRutaRecordada(RUTA_DETALLE_PEDIDO_KEY, location.pathname, location.search, almacenSesion())
  }, [cabecera, location.pathname, location.search])

  function volver() {
    // «Atrás» es la señal inequívoca de haber terminado con este pedido.
    olvidarRutaRecordada(RUTA_DETALLE_PEDIDO_KEY, almacenSesion())
    // Volver por historial conserva filtros, página y scroll de la lista. Si se
    // ha llegado por URL directa no hay historial que deshacer y se va a la lista.
    if (volverA) navigate(-1)
    else navigate('/pedidos-ventas')
  }

  const pageBg = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'

  if (!cabecera && (cargandoCabecera || detalle.isLoading)) {
    return (
      <div className={`flex flex-col gap-4 ${pageBg}`}>
        <Loader label={t('salesOrders.loading')} />
      </div>
    )
  }

  if (!cabecera) {
    return (
      <div className={`flex flex-col gap-4 ${pageBg}`}>
        <EmptyState
          dark={dark}
          title={t('salesOrders.detalle.noEncontrado', { pedido: salesid })}
          description={t('salesOrders.detalle.noEncontradoSub')}
          action={
            <button
              type="button"
              // `volver()` y no `navigate`: además de llevar a la lista, OLVIDA
              // el recuerdo. Si no, la lista nos devolvería aquí.
              onClick={volver}
              className="px-3 min-h-[34px] rounded-lg text-[12px] font-semibold bg-sky-600 text-white hover:bg-sky-500 transition-colors cursor-pointer"
            >
              {t('salesOrders.title')}
            </button>
          }
        />
      </div>
    )
  }

  // El overlay de Dataverse todavía no tiene ni cargas ni facturas: no se
  // pintan pestañas vacías que parecerían un pedido sin actividad.
  const sinConsolidar = !!cabecera.pendienteSync

  // Exactamente el libro de la lista, con este pedido solo. No mira la pestaña
  // abierta, ni el scroll, ni el selector pendientes/facturadas: se exporta el
  // pedido entero, siempre lo mismo.
  async function exportar() {
    if (exportando || !cabecera) return
    setExportando(true)
    try {
      await exportarPedidosVenta([filaDeCabecera(cabecera)], { isEmpresaAll, t, fmtArt })
    } finally {
      setExportando(false)
    }
  }

  return (
    <div className={`flex flex-col gap-4 ${pageBg}`}>
      <CabeceraPedido
        dark={dark}
        cabecera={cabecera}
        isEmpresaAll={isEmpresaAll}
        parcial={origen === 'derivada'}
        puedeCrearCarga={puedeCrearCarga && lineas.length > 0}
        isFetching={detalle.isFetching}
        onVolver={volver}
        onAdjuntos={() => setAdjuntosAbierto(true)}
        onDocumentos={() => tabs.irA('documentos')}
        onNuevaCarga={() => { tabs.irA('cargas'); setNuevaCarga(true) }}
        onExportar={exportar}
        exportando={exportando}
        onRefrescar={detalle.refetch}
      />

      {sinConsolidar ? (
        <div className={`rounded-xl border px-4 py-3 text-[12px] ${
          dark ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
               : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}>
          {t('salesOrders.sync.linesNotice')}
        </div>
      ) : detalle.isError ? (
        <p className={`text-[12px] font-semibold ${dark ? 'text-red-400' : 'text-red-600'}`}>
          {t('salesOrders.line.error')}
        </p>
      ) : (
        <>
          <TabsPedido
            dark={dark}
            tabs={definicionTabs}
            activa={tabs.tab}
            onCambio={tabs.irA}
          />

          {/* Una sección cada vez. Es todo el truco: la página mide lo que mide
              la pestaña abierta, no la suma del pedido entero.

              El `key` es la pestaña para que la entrada se repita en cada
              cambio: sin él React reutiliza el nodo y la animación sólo correría
              la primera vez. `pv-panel-in` es una entrada corta, sin `fill-mode`, para que un
              render sin animaciones nunca deje el panel invisible. */}
          <div key={tabs.tab} role="tabpanel" className="pt-1 pv-panel-in">
            {tabs.tab === 'resumen' && (
              <ResumenBento
                dark={dark}
                cabecera={cabecera}
                lineas={lineas}
                cargas={cargas}
                porCarga={porCarga}
                solicitudes={solicitudes}
                qtySinLanzar={qtySinLanzar}
                alertas={alertas}
                isEmpresaAll={isEmpresaAll}
                onIrACargas={() => tabs.irA('cargas')}
              />
            )}

            {tabs.tab === 'cargas' && (
              <SeccionCargas
                dark={dark}
                empresa={cabecera.empresa}
                numPedido={salesid}
                cargas={cargas}
                lineas={lineas}
                solicitudes={solicitudes}
                lineaPorTransid={lineaPorTransid}
                descripcionPorTransid={descripcionPorTransid}
                filtro={filtroCarga}
                onFiltro={setFiltroCarga}
                puedeCrear={!!puedeCrearCarga}
                nuevaAbierta={nuevaCarga}
                onNuevaAbierta={setNuevaCarga}
                onSolicitada={() => detalle.refetch()}
              />
            )}

            {tabs.tab === 'lineas' && (
              <SeccionLineas
                dark={dark}
                sinFacturar={sinFacturar}
                facturadas={facturadas}
                pickingPorLinea={pickingPorLinea}
                lanzamientoDe={lanzamientoDe}
                albaranesDe={albaranesDe}
                cargas={cargas}
                grupo={tabs.grupo}
                onGrupo={tabs.setGrupo}
                filtros={tabs}
                facturas={facturas}
                marcas={marcasLineas}
              />
            )}

            {tabs.tab === 'facturacion' && (
              <SeccionFacturacion
                dark={dark}
                cabecera={cabecera}
                lineas={lineas}
                onVerAdjuntosFactura={(recid, factura) => setFacturaAdj({ recid, factura })}
                // Cambia de pestaña, de grupo y de filtro de una vez: el
                // contexto se conserva sin recorrer media pantalla.
                onVerLineas={tabs.verLineasDeFactura}
              />
            )}

            {tabs.tab === 'documentos' && (
              <SeccionDocumentacion
                dark={dark}
                salesid={salesid}
                empresa={cabecera.empresa}
                porCarga={porCarga}
                onVerAdjuntos={() => setAdjuntosAbierto(true)}
                onDocumentosCarga={setDocumentos}
              />
            )}

          </div>
        </>
      )}

      {adjuntosAbierto && (
        <AdjuntosModal
          dark={dark}
          pedidoId={salesid}
          queryKeyPrefix="venta-adjuntos"
          fetchAdjuntos={pedidosVentasApi.getAdjuntos}
          onClose={() => setAdjuntosAbierto(false)}
        />
      )}

      {facturaAdj && (
        <AdjuntosModal
          dark={dark}
          pedidoId={facturaAdj.factura}
          queryKeyPrefix={`venta-factura-adjuntos-${facturaAdj.recid}`}
          fetchAdjuntos={() => pedidosVentasApi.getAdjuntosFactura(facturaAdj.recid)}
          onClose={() => setFacturaAdj(null)}
        />
      )}

      {/* El cajón documental desde la pestaña de documentos. El de la pestaña de
          cargas lo monta CargasPedido por su cuenta. */}
      {documentos && (
        <CargaDocumentosDrawer
          dark={dark}
          empresa={documentos.empresa}
          numPedido={documentos.numPedido}
          carga={documentos.carga}
          estado={documentos.estado}
          paletizada={documentos.paletizada}
          aerea={documentos.aerea}
          onClose={() => setDocumentos(null)}
        />
      )}
    </div>
  )
}
